/**
 * HUB-125 — where a sign-in lands. An explicit destination (a validated
 * `return_to`, or the `redirectTo` an MFA step carried) always wins; without
 * one the admin-set `portal.landing_url` from GET /auth/sso/info applies;
 * without that, the hub root (the track list), as before.
 */
import { isPortalRoot } from '@verybigthings/trackframe-shared/portal';
import { hub } from '@/lib/hub-client';

export function landingDestination(
  explicit: string | null | undefined,
  landingUrl: string | null | undefined,
): string {
  if (explicit) return explicit;
  return landingUrl || '/';
}

/** Resolve the destination, asking the hub for the landing URL only when nothing explicit is known. */
export async function resolveLanding(explicit: string | null | undefined): Promise<string> {
  if (explicit) return explicit;
  const landingUrl = await hub.authSsoInfo().then((info) => info.landingUrl).catch(() => null);
  return landingDestination(null, landingUrl);
}

/**
 * HUB-125 D4: a set landing URL REPLACES the hub root. A signed-in visit to
 * `/` goes there; with none set (or one that is the portal root itself, which
 * would loop) the track list renders as before.
 */
export function rootRedirectTarget(landingUrl: string | null | undefined, origin: string): string | null {
  if (!landingUrl) return null;
  return isPortalRoot(landingUrl, origin) ? null : landingUrl;
}

/**
 * HUB-125 D4, boot gate. `index.html` starts the `/auth/sso/info` lookup for
 * a visit to `/` before the bundle downloads and parks the promise on
 * `window.__TF_LANDING__`; `main.tsx` awaits this BEFORE the first render, so
 * the track list never paints under the redirect. Resolves true when the
 * page is leaving (nothing may mount), false when the SPA should mount.
 * Pinned by `landing-url.test.ts` and `boot-landing-parity.test.ts`.
 */
export async function bootRedirect(
  pending: Promise<string | null> | undefined,
  loc: { pathname: string; origin: string; replace(url: string): void },
): Promise<boolean> {
  if (!pending || loc.pathname !== '/') return false;
  const target = rootRedirectTarget(await pending, loc.origin);
  if (!target) return false;
  loc.replace(target);
  return true;
}
